/**
 * Color by number — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const colorByNumberPack: ToilePack = {
    slug: 'color-by-number',
    category: 'coloring',
    name: 'Color by number',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'butterfly', label: 'Butterfly', subject: 'a wide-winged butterfly on a flowering stem, its wings split into symmetrical panels of eyespots and bands'},
        {key: 'balloon', label: 'Hot-air balloon', subject: 'a hot-air balloon over low rolling hills, its envelope banded in wide stripes above a wicker basket'},
        {key: 'parrot', label: 'Parrot', subject: 'a macaw parrot on a leafy branch, its long tail sweeping down past two broad leaves'},
    ],
    styles: [
        {key: 'six', label: 'Six colors', style: 'a kindergarten six-color chart with very large regions and thick outlines, a key strip along the foot reading "1 red  2 blue  3 yellow  4 green  5 orange  6 brown"', palette: 'Black ink only', framing: 'The drawing centred large with a generous white margin.'},
        {key: 'twelve', label: 'Twelve colors', style: 'a school-age twelve-color chart with finer regions and smaller numerals, a key strip along the foot running "1" through "12" with an empty swatch square beside each', palette: 'Black ink only', framing: 'The full page used, the drawing centred above the key strip.'},
        {key: 'mosaic', label: 'Mosaic', style: 'an adult mosaic chart broken into hundreds of small faceted regions, a compact numbered key strip along the foot', palette: 'Black ink only', framing: 'The drawing filling the sheet to a narrow white margin.'},
    ],
    directions: ['a dinosaur instead', 'a Christmas scene', 'for a classroom worksheet'],
    seo: {
        title: 'Color by number: butterfly, balloon, parrot',
        metaDescription: 'Nine color by number pages — butterfly, hot-air balloon, parrot — six, twelve or mosaic colors, a numbered key on every page.',
        h1: 'Color by number: butterfly, hot-air balloon and parrot in three difficulties',
        tagline: 'Nine color-by-number pages, three pictures, three difficulties each — a numbered key at the foot of every page.',
        intro: 'The picture divided into numbered regions with a color key at the bottom: a butterfly, a hot-air balloon, a parrot. Six colors and big regions for kindergarten, twelve colors and finer regions for older kids, a fine mosaic version for adults. Each page prints with its own key strip — pick a difficulty and start filling.',
        useCases: [
            'a kindergarten color-recognition activity',
            'a classroom quiet-time worksheet',
            'an adult mosaic coloring session',
            'a travel activity book page',
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn at portrait A4 proportions (832 by 1216) with a white margin, so it prints clean on standard paper.'},
            {q: 'Does each page include the color key?', a: 'Yes — a numbered key strip prints along the foot of every page, matched to how many colors that difficulty uses.'},
            {q: 'Can I get a different picture?', a: 'Yes — that is what the direction field on the page is for. Type a different subject and it is drawn again at all three difficulties.'},
        ],
    },
}
